"use client";

import { capText, PromoIcon, PromoStrip, PromoTag } from "@/components/fantasy-props/promo";
import { hitPillClass } from "@/components/fantasy-props/board-parts";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { PlayerAvatar } from "@/components/ui/avatar";
import { playerTag } from "@/lib/fantasy-props/player-tag";
import { memo, useMemo, type ReactNode } from "react";
import { AnimatePresence, m as motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { ArrowUpRight, Check, ChevronDown, ChevronRight, ChevronUp, EyeOff, Info, MessageCirclePlus, Plus } from "lucide-react";
import Link from "next/link";
import type { EventHeader } from "@/lib/odds-screen/live/types";
import { LatestPlay, LiveState } from "@/components/odds-screen/live/live-state";
import { scoreLine } from "@/components/odds-screen/live/labels";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { OddsToHitTooltipContent } from "../odds-to-hit-tooltip";
import { formatSideLabel, isYesNoSide } from "../odds-labels";
import { normalizeOddsToHitPercent, resolveEffectiveSlipContext } from "../props-slip-breakevens";
import { ShareButton } from "@/components/slip/share-button";
import {
  DisplayPlayerProp,
  DisplayedOddsResult,
  PlatformType,
  getDisplayedOdds,
  getRowKey,
} from "../utils-v2";
import { StakeButton, StakeLabel, stakeSpoken } from "@/components/odds/stake-tip";
import { WhalesCell, WhalesLogoMark } from "@/components/odds/whales";
import { useWhalesSlot } from "@/hooks/use-whales-gate";
import { rowWhalesSpec } from "../utils-v2/displayed-odds";
import type { WhalesSpec } from "@/lib/whales";
import { columnBooks, type BoardColumn } from "@/components/fantasy-props/model";
import { PLATFORM_COLUMN_KEY } from "../table-styles";
import {
  formatPercentage,
  getBaselineOddsForRow,
  isAltBetterThanSelected,
} from "../desktop-optimizer-table.helpers";
import type { TableVariant } from "../utils-v2";
import type { SportsbookId } from "@/lib/sportsbook-registry";
import { getBookDisplayName } from "@/lib/utils/sports-books";
import { americanToDecimal, type OddsFormat } from "@/utils/calculators/odds-utils";
import { phCapture, trackBetLinkClick } from "@/utils/analytics";
import { resolveBetLink, toBetLinkSet } from "@/lib/bet-links";
import { rowIdOf } from "@/lib/fantasy-props/slip";
import { UNGRADED } from "@/lib/fantasy-props/rows";
import type { LapsedProps } from "@/hooks/use-order-hold";
import {
  displayPlayerPropToComparisonData,
  type BookOddsData,
} from "../comparison-table-types";
import {
  canOpenBookHistory,
  findBookSeries,
  useEventLineHistory,
} from "@/components/odds/line-sparkline";
import {
  canAnchorOddsHistory,
  teamsForSide,
  type OddsHistoryPeerBook,
  type OddsHistorySelection,
  type OddsHistorySelectionContext,
  type OpenOddsHistory,
} from "@/components/odds/odds-history-selection";
import { OddsHistoryTrigger } from "@/components/odds/odds-history-trigger";
import { bestAtOwnLine, rowHistoryOpposite } from "@/components/optimizer/history-opposite";

const RE_AT_SYMBOL = /\s*@\s*/;
const RE_TIMEZONE = /\s+(EST|CST|MST|PST|EDT|CDT|MDT|PDT)$/i;

interface MobileOptimizerCardProps {
  row: DisplayPlayerProp;
  isExpanded: boolean;
  onToggle: (id: string, row: DisplayPlayerProp) => void;
  onShare: (row: DisplayPlayerProp) => void;
  onChatShare?: (row: DisplayPlayerProp) => void;
  onDismiss?: (row: DisplayPlayerProp) => void;
  slipState?: "in" | "out";
  onSlipToggle?: (row: DisplayPlayerProp) => void;
  leagueTag?: string;
  liveEvent?: EventHeader;
  tableVariant: TableVariant;
  platform: SportsbookId;
  columns: readonly BoardColumn[];
  oddsFormat?: OddsFormat;
  isFirstCard?: boolean;
  oddsScreenHref: string | null;
  onOpenHistory: OpenOddsHistory;
  lapsed: LapsedProps;
}


const toTitleCase = (str: string): string => {
  return str
    .split(" ")
    .map((word) => {
      if (word === word.toUpperCase() && word.length <= 4) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

const buildBetSelection = (row: DisplayPlayerProp): string => {
  const betType = row.bet_type?.toUpperCase() ?? "";
  const line = row.line;
  const market = (row.market_display ?? row.stat_type ?? "").toLowerCase();

  if (row.market_type === "game" && row.selection_label) {
    return row.selection_label;
  }

  if (isYesNoSide(betType)) {
    return formatSideLabel(betType);
  }

  const formatLineValue = (val: number, isSpread: boolean): string => {
    if (isSpread) {
      return val >= 0 ? `+${val}` : `${val}`;
    }
    return String(val);
  };

  const isSpread = market.includes("spread") || market.includes("handicap");
  const isMoneyline = market.includes("moneyline") || /\bml\b/.test(market);
  const isTotal = market.includes("total") || betType === "OVER" || betType === "UNDER";

  const isGameLine = betType === "HOME" || betType === "AWAY" || isSpread || isMoneyline;

  const playerNameLooksLikeEvent = row.player_name &&
    (row.player_name.includes("@") || /\bvs\.?\b/i.test(row.player_name));

  if (row.player_name && !isGameLine && !playerNameLooksLikeEvent) {
    const side = betType === "OVER" ? "Over" : betType === "UNDER" ? "Under" : betType;
    if (typeof line === "number") {
      return `${row.player_name} ${side} ${line}`;
    }
    return row.player_name;
  }

  if (isTotal && row.selection_label) {
    return row.selection_label;
  }

  if (isTotal && typeof line === "number") {
    const side = betType === "OVER" ? "Over" : betType === "UNDER" ? "Under" : betType;
    return `${side} ${line}`;
  }

  if (isSpread && typeof line === "number") {
    const teamName = betType === "HOME" || betType === "AWAY"
      ? toTitleCase(row.team ?? "")
      : betType;
    return `${teamName} ${formatLineValue(line, true)}`;
  }

  if (row.selection_label) {
    return row.selection_label;
  }

  if (row.team) {
    return toTitleCase(row.team);
  }

  return betType || "Unknown";
};

const buildMarketLabel = (row: DisplayPlayerProp): string => {
  return row.market_display ?? row.stat_type ?? "";
};

const buildGameMeta = (row: DisplayPlayerProp): string => {
  const parts: string[] = [];

  if (row.event_short) {
    parts.push(row.event_short);
  } else if (row.event_name) {
    parts.push(row.event_name);
  } else if (row.team) {
    parts.push(row.team.toUpperCase());
  }

  if (row.game_start_time) {
    let time = row.game_start_time;
    time = time.replace(RE_AT_SYMBOL, " ");
    time = time.replace(RE_TIMEZONE, "");
    parts.push(time);
  }

  return parts.join(" • ");
};

function toDecimalOdds(raw: unknown): number {
  const american = Number(raw);
  return Number.isFinite(american) && american !== 0 ? americanToDecimal(american) : 0;
}

const PILL = "data-num inline-flex h-7 flex-shrink-0 items-center rounded-md px-2 text-[13px] font-semibold tabular-nums";
const ICON_BUTTON = "inline-flex h-11 w-10 items-center justify-center rounded-md text-zinc-400 active:bg-white/[0.08] hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50";
const PRICE_ROW = "flex h-11 items-center gap-2.5 border-t border-white/[0.06] text-[14px]";
const CTA = "data-num inline-flex h-9 flex-shrink-0 items-center gap-2 rounded-md bg-accent-value px-3 text-[13px] font-bold text-zinc-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

type CardColumn =
  | { readonly kind: "book"; readonly bookId: SportsbookId; readonly odds: DisplayedOddsResult; readonly isBetter: boolean; readonly decimalOdds: number }
  | { readonly kind: "whales" };

function evTone(ev: number): string {
  return ev > 0 ? "bg-accent-value/15 text-accent-value" : "bg-white/[0.04] text-zinc-400";
}

function priceText(odds: DisplayedOddsResult): string {
  return odds.multiplierText ?? odds.oddsText ?? "–";
}

function priceTone(odds: DisplayedOddsResult, isPrimary: boolean, isBetter: boolean): string {
  if (isPrimary) return "text-primary";
  if (odds.multiplierText) return odds.multiplierText.startsWith("0.") ? "text-warning" : "text-accent-value";
  return isBetter ? "text-accent-value-strong" : "text-foreground/85";
}

export const MobileOptimizerCard = memo(function MobileOptimizerCard({
  row,
  isExpanded,
  onToggle,
  onShare,
  onChatShare,
  onDismiss,
  slipState,
  onSlipToggle,
  leagueTag,
  liveEvent,
  tableVariant,
  platform,
  columns,
  oddsFormat = "american",
  isFirstCard,
  oddsScreenHref,
  onOpenHistory,
  lapsed,
}: MobileOptimizerCardProps) {
  const isPropsVariant = tableVariant === "props";
  const hitPercent = isPropsVariant ? null : normalizeOddsToHitPercent(row.odds_to_hit);
  const evPct = Number(row.ev_percentage ?? 0);
  const rawHit = Number(row.odds_to_hit ?? row.consensus_implied_probability ?? 0);
  const hitPct = rawHit < 1 ? rawHit * 100 : rawHit;
  const grading = row.grading ?? UNGRADED;
  const breakevenPct = isPropsVariant && grading.kind === "graded"
    ? row.effective_slip_breakeven_pct ?? resolveEffectiveSlipContext({
        platform: platform as PlatformType,
        rawSlipTypeId: null,
        americanOdds: typeof row.primary_odds === "number" ? row.primary_odds : null,
        standardDecimal: grading.standard,
      }).effectiveBreakevenPct
    : null;
  const metricText = isPropsVariant ? `${formatPercentage(hitPct)}%` : row.ev_percentage === null ? "–" : `${evPct > 0 ? "+" : ""}${formatPercentage(evPct)}% EV`;
  const metricClass = isPropsVariant ? hitPillClass(hitPct, breakevenPct) : cn(PILL, evTone(evPct));

  const platformColumnKey = PLATFORM_COLUMN_KEY[platform];
  const baselineOdds = platformColumnKey ? getBaselineOddsForRow(row, platformColumnKey) : null;
  const primaryOdds = platformColumnKey
    ? getDisplayedOdds(row, platformColumnKey, { bookId: platform, isPrimary: true, isComparison: false, oddsFormat })
    : null;

  const orderedBooks = useMemo(() => [platform, ...columnBooks(columns).filter((book) => book !== platform)], [columns, platform]);
  const whalesColumn = columns.some((column) => column.kind === "whales");
  const whalesSpec = whalesColumn ? rowWhalesSpec(row, "side") : null;
  const whalesShown = useWhalesSlot(row) && whalesColumn;
  const shown = useMemo(() => {
    const list: CardColumn[] = [];
    for (const column of columns) {
      if (column.kind === "whales") {
        if (whalesShown) list.push(column);
        continue;
      }
      const bookId = column.book;
      if (bookId === platform) continue;
      const columnKey = PLATFORM_COLUMN_KEY[bookId];
      if (!columnKey) continue;
      const odds = getDisplayedOdds(row, columnKey, { bookId, isPrimary: false, isComparison: true, oddsFormat });
      if (odds.isMissing) continue;
      const isBetter = !odds.lineBadge && isAltBetterThanSelected(row, columnKey, platformColumnKey ?? "", baselineOdds);
      list.push({ kind: "book", bookId, odds, isBetter, decimalOdds: toDecimalOdds(row[columnKey as keyof typeof row]) });
    }
    return list;
  }, [baselineOdds, columns, oddsFormat, platform, platformColumnKey, row, whalesShown]);
  const books = useMemo(() => shown.flatMap((column) => (column.kind === "book" ? [column] : [])), [shown]);
  const bestDecimal = books.reduce((max, book) => (book.isBetter ? Math.max(max, book.decimalOdds) : max), 0);

  const historyEventId = typeof row.event_id === "string" ? row.event_id : "";
  const historySide = (row.selection_side ?? row.bet_type ?? "OVER") as string;
  const historySideLc = historySide.toLowerCase();
  const historyEventStart = row.game_start_timestamp ? new Date(row.game_start_timestamp) : null;

  const comparisonBooksById = useMemo(() => {
    const map = new Map<string, BookOddsData>();
    if (!isExpanded) return map;
    for (const book of displayPlayerPropToComparisonData(row, orderedBooks, platform, true).books) {
      map.set(book.book, book);
    }
    return map;
  }, [isExpanded, orderedBooks, platform, row]);

  const historySelectionKeys = useMemo(() => {
    const ids = new Set<string>();
    for (const book of comparisonBooksById.values()) {
      if (book.selectionId) ids.add(book.selectionId);
    }
    return [...ids].map((id) => `${id}|${historySideLc}`);
  }, [comparisonBooksById, historySideLc]);

  const lineHistory = useEventLineHistory(
    historyEventId,
    isExpanded && Boolean(historyEventId) && historySelectionKeys.length > 0,
    historyEventStart,
    historySelectionKeys,
  );

  const linksFor = (bookId: string, resolved: string | null | undefined) =>
    row.__comparison_links?.[bookId] ?? toBetLinkSet(resolved, undefined, undefined) ?? undefined;

  const rowHistoryContext: OddsHistorySelectionContext = {
    fair:
      row.consensus_implied_probability != null && row.consensus_implied_probability > 0 && row.consensus_implied_probability < 1
        ? { display: "probability", probability: row.consensus_implied_probability }
        : undefined,
    value:
      row.ev_percentage != null && Number.isFinite(row.ev_percentage)
        ? { kind: "probability_edge", percent: row.ev_percentage }
        : undefined,
  };

  const altBest = useMemo(() => bestAtOwnLine([...comparisonBooksById.values()], (book) => book.selectionOdds), [comparisonBooksById]);
  const historyPeers = useMemo(() => {
    const peers: OddsHistoryPeerBook[] = [];
    for (const book of comparisonBooksById.values()) {
      if (typeof book.selectionOdds !== "number" || !Number.isFinite(book.selectionOdds)) continue;
      const betLinks = linksFor(book.book, book.betLink);
      peers.push({
        book: book.book,
        bookDisplayName: getBookDisplayName(book.book),
        ...(book.selectionId ? { selectionId: book.selectionId } : {}),
        line: book.line ?? row.line ?? null,
        oddsAmerican: book.selectionOdds,
        ...(betLinks ? { betLinks } : {}),
        ...(book.isBestSelection === true || altBest(book) ? { isBestPrice: true as const } : {}),
      });
    }
    return peers;
  }, [comparisonBooksById, altBest, row.line, row.__comparison_links]);

  const renderHistoryTrigger = (bookId: SportsbookId): ReactNode => {
    const book = comparisonBooksById.get(bookId);
    if (!book) return null;
    const selection: OddsHistorySelection = {
      eventId: historyEventId,
      book: bookId,
      selectionId: book.selectionId ?? undefined,
      side: historySide,
      line: book.line ?? row.line ?? null,
      market: row.stat_type,
      marketLabel: row.market_display ?? row.stat_type,
      entity: row.player_name,
      eventStart: row.game_start_timestamp ?? null,
      matchup: row.event_name ?? row.player_name,
      teams: teamsForSide(historySide, row.team, row.opponent, row.team_short, row.opponent_short),
      opposite: rowHistoryOpposite(row, [...comparisonBooksById.values()], bookId),
      context: {
        ...(book.line === undefined ? rowHistoryContext : {}),
        quote: {
          oddsAmerican: typeof book.selectionOdds === "number" && Number.isFinite(book.selectionOdds) ? book.selectionOdds : undefined,
          betLinks: linksFor(bookId, book.betLink),
          isBestPrice: book.isBestSelection === true || altBest(book) ? true : undefined,
        },
      },
    };
    const series = book.selectionId ? findBookSeries(lineHistory, { book: bookId, selectionId: book.selectionId, side: historySide }) : null;
    if (!canAnchorOddsHistory(selection) || !canOpenBookHistory(series, { book: bookId, selectionId: book.selectionId, side: historySide })) {
      return null;
    }
    return (
      <OddsHistoryTrigger
        mode="inline"
        bookLabel={getBookDisplayName(bookId)}
        onOpen={() => onOpenHistory({ selection, fallbackSeries: series, peers: historyPeers })}
        className="h-11 w-11 border-0 bg-transparent px-0 text-zinc-500 hover:bg-transparent hover:text-foreground"
      />
    );
  };

  const resolveComparisonLink = (bookId: string) => resolveBetLink(row.__comparison_links?.[bookId], true);
  const primaryLink = resolveBetLink(row.__comparison_links?.[platform], true) ?? row.bet_link ?? null;
  const trackPrimaryClick = () => {
    trackBetLinkClick({
      sportsbook: platform,
      page: "optimizer",
      bet_type: row.market_type === "game" ? "gameline" : "prop",
      odds_american: Number(row.primary_odds) || undefined,
      ev_percentage: Number(row.ev_percentage) || undefined,
      league: (row.league ?? row.sport ?? undefined) as string | undefined,
    });
  };
  const trackComparisonClick = (bookId: string) => {
    trackBetLinkClick({
      sportsbook: bookId,
      page: "optimizer",
      bet_type: row.market_type === "game" ? "gameline" : "prop",
      ev_percentage: Number(row.ev_percentage) || undefined,
      league: (row.league ?? row.sport ?? undefined) as string | undefined,
      click_source: "peer_grid",
    });
  };

  const betSelection = buildBetSelection(row);
  const marketLabel = buildMarketLabel(row);
  const gameMeta = buildGameMeta(row);
  const live = liveEvent?.live;
  const score = liveEvent === undefined ? null : scoreLine(liveEvent);
  const tag = isPropsVariant ? playerTag(row) : null;
  const primaryText = primaryOdds ? priceText(primaryOdds) : null;
  const primaryName = getBookDisplayName(platform);

  const historyTriggers = new Map<string, ReactNode>();
  if (isExpanded) {
    historyTriggers.set(platform, renderHistoryTrigger(platform));
    for (const { bookId } of books) historyTriggers.set(bookId, renderHistoryTrigger(bookId));
  }
  const anyHistoryTrigger = [...historyTriggers.values()].some(Boolean);

  const metricPill = (
    <span className={metricClass} {...(isFirstCard && !isPropsVariant ? { "data-tour": "mobile-ev-badge" } : {})}>
      {metricText}
      {isPropsVariant && <Info className="ml-1 size-3 opacity-60" aria-hidden="true" />}
    </span>
  );

  return (
    <div
      {...(isExpanded ? {} : lapsed)}
      className={cn("w-full rounded-xl border bg-surface-raised", isExpanded ? "border-white/[0.14]" : "border-surface-line")}
      {...(isFirstCard ? { "data-tour": "mobile-card" } : {})}
    >
      <section
        {...(isExpanded ? lapsed : {})}
        role="button"
        tabIndex={0}
        onClick={() => onToggle(rowIdOf(row), row)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle(rowIdOf(row), row);
          }
        }}
        className="w-full cursor-pointer rounded-xl px-3.5 pb-3 pt-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 active:bg-white/[0.04]"
        aria-expanded={isExpanded}
        aria-controls={`card-details-${getRowKey(row)}`}
      >
        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            {isPropsVariant && <PlayerAvatar name={row.player_name} imageUrl={row.player_image_url ?? null} size={32} />}
            <div className="flex min-w-0 flex-1 flex-col">
              {(score || gameMeta || leagueTag) && (
                <span className="line-clamp-2 text-[11px] leading-4 text-zinc-500">
                  {leagueTag && <span className="mr-1.5 inline-block rounded bg-white/[0.06] px-1 align-[1px] text-[10px] font-semibold leading-[14px] tracking-[0.04em] text-zinc-400">{leagueTag}</span>}
                  {score === null ? gameMeta : <span data-testid="live-score-line" className="data-num font-medium text-zinc-300">{score}</span>}
                </span>
              )}
              <span className="flex items-start gap-1.5 text-[13px] font-semibold leading-[18px] text-foreground">
                <span className="min-w-0 text-pretty [overflow-wrap:anywhere]">
                  {betSelection}
                  {row.promo?.originalLine != null && row.promo.originalLine !== row.line && <> <s className="text-zinc-500 decoration-zinc-500/70" aria-label={`was ${row.promo.originalLine}`}>{row.promo.originalLine}</s></>}
                  {tag && <span className="ml-1.5 whitespace-nowrap text-[11px] font-medium text-zinc-500">{tag}</span>}
                </span>
                {row.promo != null && <PromoIcon promo={row.promo} size={14} />}
              </span>
              {(marketLabel || live || row.promo != null) && (
                <span className="flex min-w-0 items-center gap-1.5 text-[13px] leading-[18px] text-zinc-300">
                  <span className="min-w-0 truncate">
                    {marketLabel}
                    {row.promo != null && capText(row.promo.cap) && <> <PromoTag>{capText(row.promo.cap)}</PromoTag></>}
                  </span>
                  {live && <LiveState live={live} compact className="shrink-0" />}
                </span>
              )}
              {live && <LatestPlay play={live.plays[0] ?? null} />}
            </div>
          </div>
          <div className="-mr-3 -mt-1.5 flex flex-shrink-0 items-center" onKeyDown={(event) => event.stopPropagation()}>
            {onSlipToggle && slipState ? (
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); onSlipToggle(row); }}
                onKeyDown={(event) => event.stopPropagation()}
                className={cn(ICON_BUTTON, slipState === "in" && "text-primary")}
                aria-label={`${slipState === "in" ? "Remove" : "Add"} ${row.player_name || row.selection_label || "this pick"} ${slipState === "in" ? "from" : "to"} slip`}
                title={slipState === "in" ? "Remove from slip" : "Add to slip"}
              >
                {slipState === "in" ? <Check className="size-4" strokeWidth={3} aria-hidden="true" /> : <Plus className="size-4" aria-hidden="true" />}
              </button>
            ) : null}
            <ShareButton onClick={() => onShare(row)} className="w-10 text-zinc-400 sm:h-11 sm:w-10 sm:p-0 sm:opacity-100" />
            {onChatShare ? (
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); onChatShare(row); }}
                onKeyDown={(event) => event.stopPropagation()}
                className={ICON_BUTTON}
                aria-label={`Share ${row.player_name || row.selection_label || "this pick"} to chat`}
                title="Share to chat"
              >
                <MessageCirclePlus className="size-4" aria-hidden="true" />
              </button>
            ) : null}
            {onDismiss ? (
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); onDismiss(row); }}
                onKeyDown={(event) => event.stopPropagation()}
                className={ICON_BUTTON}
                aria-label={`Hide ${row.player_name || row.selection_label || row.event_name || "this bet"}`}
                title="Hide bet"
              >
                <EyeOff className="size-4" aria-hidden="true" />
              </button>
            ) : null}
            <span className="inline-flex h-11 w-7 items-center justify-center text-zinc-500" aria-hidden="true">
              {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            </span>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2">
          {isPropsVariant ? (
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} aria-label={`${metricText} to hit, how this is priced`} className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                  {metricPill}
                </button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="start" className="glass-menu w-auto min-w-[220px] p-3" onClick={(e) => e.stopPropagation()}>
                <OddsToHitTooltipContent
                  platform={platform}
                  oddsToHitPercent={normalizeOddsToHitPercent(row.odds_to_hit)}
                  americanOdds={typeof row.primary_odds === "number" ? row.primary_odds : null}
                  grading={grading}
                  variant="cell"
                />
              </PopoverContent>
            </Popover>
          ) : metricPill}
          {isPropsVariant ? (
            <span className="whitespace-nowrap text-[12px] text-zinc-400">to hit</span>
          ) : hitPercent !== null && (
            <span className="whitespace-nowrap text-[12px] text-zinc-400">
              <span className="data-num font-semibold text-foreground">{hitPercent.toFixed(1)}%</span> to hit
            </span>
          )}
          {primaryText !== null && (
            <div className="ml-auto flex flex-shrink-0 flex-col items-end gap-0.5">
              {primaryLink ? (
                <a
                  href={primaryLink}
                  data-tour="mobile-bet"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { e.stopPropagation(); trackPrimaryClick(); }}
                  onKeyDown={(e) => e.stopPropagation()}
                  aria-label={`Place bet at ${primaryName}, ${primaryText}${stakeSpoken(primaryOdds?.stake?.stake ?? null)}`}
                  className={cn(CTA, "hover:bg-accent-value-strong")}
                >
                  <SportsbookLogoMark sportsbook={platform} size={16} decorative className="rounded-sm" />
                  <span>{primaryText}</span>
                  <span aria-hidden className="h-3.5 w-px bg-zinc-950/25" />
                  <span>Bet</span>
                  <ArrowUpRight size={13} strokeWidth={2.5} aria-hidden="true" />
                </a>
              ) : (
                <span className={cn(PILL, "gap-1.5 bg-primary/15 text-primary")}>
                  <SportsbookLogoMark sportsbook={platform} size={16} decorative className="rounded-sm" />
                  <span className="sr-only">{primaryName} </span>
                  {primaryText}
                </span>
              )}
              {primaryOdds?.stake != null && <StakeButton quote={primaryOdds.stake} format={oddsFormat} />}
            </div>
          )}
        </div>

        {!isExpanded && shown.length > 0 && (
          <div className="relative mt-3 flex items-center gap-4 overflow-x-auto border-t border-white/[0.06] pt-2 scrollbar-none [mask-image:linear-gradient(to_right,black_calc(100%-28px),transparent)] after:block after:h-px after:w-7 after:flex-shrink-0 after:content-['']">
            {shown.map((column) => {
              if (column.kind === "whales") {
                return (
                  <WhalesCell key="whales" spec={whalesSpec} format={oddsFormat} tap className="h-10 flex-shrink-0 flex-row gap-1.5 py-0 text-foreground/85">
                    <WhalesLogoMark size={20} />
                  </WhalesCell>
                );
              }
              const { bookId, odds, isBetter } = column;
              const link = resolveComparisonLink(bookId);
              const chip = (
                <>
                  <SportsbookLogoMark sportsbook={bookId} size={20} decorative />
                  {link === null && <span className="sr-only">{getBookDisplayName(bookId)} </span>}
                  <span className={cn("data-num text-[13px] font-semibold tabular-nums", priceTone(odds, false, isBetter))}>{priceText(odds)}</span>
                  {odds.lineBadge && <span className="data-num text-[10px] font-medium text-zinc-500">{odds.lineBadge}</span>}
                </>
              );
              const chipClass = "flex h-10 flex-shrink-0 items-center gap-1.5";
              if (link === null) return <span key={bookId} className={chipClass}>{chip}</span>;
              return (
                <a
                  key={bookId}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Place bet at ${getBookDisplayName(bookId)}, ${priceText(odds)}`}
                  onClick={(event) => { event.stopPropagation(); trackComparisonClick(bookId); }}
                  onKeyDown={(event) => event.stopPropagation()}
                  className={cn(chipClass, "rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40")}
                >
                  {chip}
                </a>
              );
            })}
          </div>
        )}
      </section>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            id={`card-details-${getRowKey(row)}`}
            role="region"
            aria-label="Odds comparison"
            className="overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {row.promo != null && <PromoStrip promo={row.promo} className="mx-3.5 mb-2 rounded-md" />}
            <div className="px-3.5">
              {primaryOdds !== null && (
                <ComparisonRow
                  bookId={platform}
                  odds={primaryOdds}
                  tone={priceTone(primaryOdds, true, false)}
                  link={primaryLink}
                  onClick={trackPrimaryClick}
                  history={historyTriggers.get(platform) ?? null}
                  anyHistory={anyHistoryTrigger}
                  primary
                  note={isPropsVariant && row.promo != null ? capText(row.promo.cap) : null}
                />
              )}
              {shown.map((column) => column.kind === "whales"
                ? <WhalesRow key="whales" spec={whalesSpec} format={oddsFormat} anyHistory={anyHistoryTrigger} />
                : (
                  <ComparisonRow
                    key={column.bookId}
                    bookId={column.bookId}
                    odds={column.odds}
                    tone={priceTone(column.odds, false, column.isBetter)}
                    best={column.isBetter && column.decimalOdds === bestDecimal}
                    link={resolveComparisonLink(column.bookId)}
                    onClick={() => trackComparisonClick(column.bookId)}
                    history={historyTriggers.get(column.bookId) ?? null}
                    anyHistory={anyHistoryTrigger}
                  />
                ))}
            </div>
            {oddsScreenHref && (
              <Link
                href={oddsScreenHref}
                onClick={(e) => { e.stopPropagation(); phCapture("odds_screen_prop_link_clicked", { surface: "mobile_card" }); }}
                className="flex h-11 items-center gap-2 border-t border-white/[0.06] px-3.5 text-[12px] font-medium text-zinc-300 no-underline active:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
              >
                All lines on Odds Screen
                <ChevronRight size={14} strokeWidth={2.2} className="ml-auto text-zinc-500" aria-hidden="true" />
              </Link>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

function WhalesRow({ spec, format, anyHistory }: { spec: WhalesSpec | null; format: OddsFormat; anyHistory: boolean }) {
  return (
    <div className={cn(PRICE_ROW, anyHistory && "pr-8")}>
      <WhalesCell spec={spec} format={format} tap className="h-11 min-h-0 min-w-0 flex-1 flex-row justify-start gap-2.5 rounded-none px-0 py-0 text-left text-[14px] text-foreground/85">
        <WhalesLogoMark size={20} />
        <span className="min-w-0 flex-1 truncate font-medium text-zinc-300">Whales</span>
      </WhalesCell>
      <span className="w-3 flex-shrink-0" aria-hidden="true" />
    </div>
  );
}

function ComparisonRow({ bookId, odds, tone, best = false, primary = false, link, onClick, history, anyHistory, note = null }: {
  bookId: SportsbookId;
  odds: DisplayedOddsResult;
  tone: string;
  best?: boolean;
  primary?: boolean;
  link: string | null;
  onClick: () => void;
  history: ReactNode;
  anyHistory: boolean;
  note?: string | null;
}) {
  const name = getBookDisplayName(bookId);
  const text = priceText(odds);
  const opposite = odds.oppositeOddsText && odds.oppositeSide ? `, ${odds.oppositeSide} ${odds.oppositeOddsText}${stakeSpoken(odds.oppositeStake)}` : "";
  const quote = `${text}${stakeSpoken(odds.stake?.stake ?? null)}${opposite}`;
  const body = (
    <>
      <SportsbookLogoMark sportsbook={bookId} size={20} decorative />
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className={cn("truncate", primary ? "font-semibold text-foreground" : "font-medium text-zinc-300")}>{name}</span>
        {odds.lineBadge && <span className="data-num flex-shrink-0 text-[10px] font-medium text-zinc-500">Alt {odds.lineBadge}</span>}
        {note && <span className="flex-shrink-0 text-[10px] font-medium text-zinc-500">{note}</span>}
      </span>
      <span className="flex flex-shrink-0 flex-col items-end leading-tight">
        <span className={cn("data-num font-semibold tabular-nums", tone, best && "text-accent-value-strong")}>
          {best && <span className="sr-only">Best price. </span>}
          {text}{" "}
          {odds.stake !== null && <StakeLabel stake={odds.stake.stake} />}
        </span>
        {odds.oppositeOddsText && odds.oppositeSide && (
          <span className="data-num text-[10px] font-medium tabular-nums text-zinc-500">
            {odds.oppositeSide} {odds.oppositeOddsText}{" "}
            {odds.oppositeStake !== null && <StakeLabel stake={odds.oppositeStake} />}
          </span>
        )}
      </span>
      <span className="flex w-3 flex-shrink-0 justify-end" aria-hidden="true">
        {link !== null && <ArrowUpRight size={12} strokeWidth={2.5} className="text-primary/60" />}
      </span>
    </>
  );
  const wash = primary && "-mx-3.5 bg-primary/[0.06] px-3.5";
  const rowClass = cn(PRICE_ROW, "min-w-0 flex-1", anyHistory ? "border-t-0" : wash, link !== null && "active:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40");
  const main = link === null
    ? <div className={rowClass} aria-label={`${name} ${quote}`}>{body}</div>
    : <a href={link} target="_blank" rel="noopener noreferrer" aria-label={`Place bet at ${name}, ${quote}`} onClick={(event) => { event.stopPropagation(); onClick(); }} className={rowClass}>{body}</a>;
  if (!anyHistory) return main;
  return (
    <div className={cn("flex items-center border-t border-white/[0.06]", wash)}>
      {main}
      <span className="-mr-3 flex h-11 w-11 flex-shrink-0 items-center justify-center">{history}</span>
    </div>
  );
}
